/**
 * Ko'rish sozlamalari: mavzu (yorug'/qorong'i/yuqori kontrast) va matn o'lchami.
 * Tanlov `localStorage` da saqlanadi va `index.html` dagi kichik skript orqali
 * render boshlanishidan oldin qo'llanadi (chaqnash bo'lmasligi uchun).
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

export type Theme = "light" | "dark" | "hc";
export type TextScale = "normal" | "large";

interface PrefsValue {
  theme: Theme;
  textScale: TextScale;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  toggleTextScale: () => void;
}

const PrefsContext = createContext<PrefsValue | null>(null);

const THEME_ORDER: Theme[] = ["light", "dark", "hc"];

function read<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key) as T | null;
    if (value && allowed.includes(value)) return value;
  } catch {
    /* localStorage o'chirilgan */
  }
  return fallback;
}

function persist(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* saqlab bo'lmadi */
  }
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() =>
    read("sf.theme", THEME_ORDER, "light"),
  );
  const [textScale, setTextScale] = useState<TextScale>(() =>
    read("sf.textScale", ["normal", "large"] as const, "normal"),
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    persist("sf.theme", theme);
  }, [theme]);

  useEffect(() => {
    if (textScale === "large") document.documentElement.dataset.scale = "large";
    else delete document.documentElement.dataset.scale;
    persist("sf.textScale", textScale);
  }, [textScale]);

  const cycleTheme = useCallback(() => {
    setThemeState((current) => {
      const next = THEME_ORDER[(THEME_ORDER.indexOf(current) + 1) % THEME_ORDER.length];
      return next;
    });
  }, []);

  const toggleTextScale = useCallback(() => {
    setTextScale((current) => (current === "large" ? "normal" : "large"));
  }, []);

  const value = useMemo<PrefsValue>(
    () => ({ theme, textScale, setTheme: setThemeState, cycleTheme, toggleTextScale }),
    [theme, textScale, cycleTheme, toggleTextScale],
  );

  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs faqat <PrefsProvider> ichida ishlaydi");
  return ctx;
}
